// cart-api.js — Cart API queue + utilities
// Replaces vendor.js module 67367
(function() {
  'use strict';

  let cartCache = null;
  let queue = Promise.resolve();

  function enqueue(fn) {
    // Run fn exactly once. The tail must never reject (else the next enqueue is skipped)
    // and must never re-run fn — add/change/update are NOT idempotent.
    const run = queue.then(fn);
    queue = run.catch(function () {});
    return run;
  }

  // Bundled section rendering: mutation responses carry the drawer HTML — pull it out,
  // hand it to the section controller, and keep the JSON payload clean for cache/events.
  function dispatchBundledSection(payload) {
    if (!payload || !payload.sections) return;
    const html = payload.sections['cart-contents'];
    delete payload.sections;
    if (!html) return;
    // Guard: a bundled render with ZERO item rows while this very response's cart has
    // renderable items is stale/inconsistent — don't inject it. The drawer's fallback
    // fetch (cache-busted, self-validating) will supply good markup instead.
    if (html.indexOf('c-cart-product') === -1) {
      // change/update responses are carts (.items); add.js responds with the added ITEM (.key).
      const renderable = payload.items
        ? payload.items.filter((i) => !(i.properties && i.properties._tote === 'true')).length
        : (payload.key ? 1 : 0);
      if (renderable > 0) return;
    }
    document.dispatchEvent(new CustomEvent('cart:section-html', { detail: { html } }));
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    cartCache = await res.json();
    return cartCache;
  }

  const CartAPI = {
    async add(variantId, quantity = 1, properties = {}) {
      // Escape hatch signal: lets the drawer open with its skeleton if the mutation is slow.
      document.dispatchEvent(new CustomEvent('cart:mutation-pending'));
      return enqueue(async () => {
       try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity, properties, sections: 'cart-contents', sections_url: window.location.pathname })
        });
        if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
        const item = await res.json();
        dispatchBundledSection(item);
        cartCache = null;
        // Fetch full cart for count + drawer
        const cart = await fetchCart();
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: item }));
        document.dispatchEvent(new CustomEvent('onCartUpdated', { detail: cart }));
        document.dispatchEvent(new CustomEvent('onCartFinished', { detail: cart }));
        return item;
       } catch (err) {
        // Any failure — HTTP or network — must tell the drawer (skeleton recovery + pending clear).
        document.dispatchEvent(new CustomEvent('cart:mutation-failed'));
        throw err;
       }
      });
    },

    // Rapid +/- taps coalesce into ONE request per line: /cart/change.js sets the
    // ABSOLUTE quantity, so only the last value in a 400ms burst matters. The
    // pending event fires on the FIRST tap of a burst so row feedback is instant.
    _qtySlots: {},

    pendingChanges() {
      const out = {};
      for (const k in this._qtySlots) out[k] = this._qtySlots[k].quantity;
      return out;
    },

    async change(line, quantity) {
      // Slots key by VARIANT id, not line key: Shopify rotates line keys on every
      // mutation, and two slots for one physical line can flush absolutes out of
      // order. One slot per variant = one serialized stream per line.
      const slotKey = (typeof line === 'string' && line.includes(':')) ? String(parseInt(line, 10)) : String(line);
      const slots = this._qtySlots;
      return new Promise((resolve, reject) => {
        let slot = slots[slotKey];
        if (!slot) {
          slot = slots[slotKey] = { waiters: [], inFlight: false };
          document.dispatchEvent(new CustomEvent('cart:mutation-pending', {
            detail: { lineKeys: (typeof line === 'string' && line.includes(':')) ? [line] : [] }
          }));
        }
        slot.quantity = quantity;
        slot.waiters.push({ resolve, reject });
        // The slot survives until its request SETTLES (pendingChanges() must cover the
        // in-flight window, or a cross-line swap regresses this line's number). A tap
        // landing mid-flight just updates the slot; settle triggers an immediate re-flush.
        if (slot.inFlight) return;
        clearTimeout(slot.timer);
        slot.timer = setTimeout(() => CartAPI._flushQty(line, slotKey), 400);
      });
    },

    _flushQty(line, slotKey) {
      const slots = this._qtySlots;
      const slot = slots[slotKey];
      if (!slot) return;
      slot.inFlight = true;
      const sentQty = slot.quantity;
      const waiters = slot.waiters;
      slot.waiters = [];
      CartAPI._changeNow(line, sentQty).then(
        (r) => {
          waiters.forEach((w) => w.resolve(r));
          // Shopify silently CLAMPS quantity to available stock (200, no error). If the
          // server gave us less than we asked, stop — re-sending queued higher values
          // would just re-clamp. The settle event carries server truth for the input.
          let achieved = null;
          try {
            const vid = parseInt(line, 10);
            const it = (r.items || []).find((i) => i.variant_id === vid);
            achieved = it ? it.quantity : 0;
          } catch (e) {}
          const clamped = achieved !== null && achieved < sentQty;
          if (!clamped && (slot.waiters.length || slot.quantity !== sentQty)) {
            slot.inFlight = false;
            CartAPI._flushQty(line, slotKey);
          } else {
            delete slots[slotKey];
            // The settle swap already re-applied pending while this slot still existed —
            // tell the drawer this line is done (with server truth) so the dim clears
            // and the input never keeps an inflated number.
            document.dispatchEvent(new CustomEvent('cart:line-settled', { detail: { lineKey: slotKey, quantity: achieved, clamped: clamped } }));
          }
        },
        (e) => {
          waiters.forEach((w) => w.reject(e));
          // Drop the slot on failure — the drawer's failed handler resyncs from the server.
          delete slots[slotKey];
        }
      );
    },

    async _changeNow(line, quantity) {
      return enqueue(async () => {
       try {
        // Shopify ROTATES a line's key on EVERY change.js mutation, and this task may
        // execute after earlier queued mutations rotated it — resolve the CURRENT key
        // HERE, at execution time, from the freshest cart snapshot.
        let sendLine = line;
        if (typeof line === 'string' && line.includes(':') && cartCache && cartCache.items) {
          const exact = cartCache.items.some((i) => i.key === line);
          if (!exact) {
            const vid = parseInt(line, 10);
            const match = cartCache.items.find((i) => i.variant_id === vid);
            if (match) sendLine = match.key;
          }
        }
        // Shopify /cart/change.js: `line` must be an integer (1-based cart index).
        // Line keys like "41921873608739:abc123" belong in the `id` param instead.
        const payload = (typeof sendLine === 'string' && sendLine.includes(':'))
          ? { id: sendLine, quantity }
          : { line: sendLine, quantity };
        payload.sections = 'cart-contents';
        payload.sections_url = window.location.pathname;
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error(`Cart change failed: ${res.status}`);
        const cart = await res.json();
        dispatchBundledSection(cart);
        cartCache = cart;
        document.dispatchEvent(new CustomEvent('onCartUpdated', { detail: cart }));
        document.dispatchEvent(new CustomEvent('onCartFinished', { detail: cart }));
        return cart;
       } catch (err) {
        // Any failure — HTTP or network — must tell the drawer, or pending UI sticks.
        document.dispatchEvent(new CustomEvent('cart:mutation-failed'));
        throw err;
       }
      });
    },

    async update(updates) {
      document.dispatchEvent(new CustomEvent('cart:mutation-pending', {
        detail: { lineKeys: Object.keys(updates || {}).filter((k) => k.includes(':')) }
      }));
      return enqueue(async () => {
       try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates, sections: 'cart-contents', sections_url: window.location.pathname })
        });
        if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);
        const cart = await res.json();
        dispatchBundledSection(cart);
        cartCache = cart;
        document.dispatchEvent(new CustomEvent('onCartUpdated', { detail: cart }));
        document.dispatchEvent(new CustomEvent('onCartFinished', { detail: cart }));
        return cart;
       } catch (err) {
        document.dispatchEvent(new CustomEvent('cart:mutation-failed'));
        throw err;
       }
      });
    },

    async get() {
      if (cartCache) return cartCache;
      return fetchCart();
    },

    async getQuantityOfVariant(variantId) {
      const cart = await this.get();
      const item = cart.items.find(i => i.variant_id === variantId);
      return item ? item.quantity : 0;
    },

    invalidate() {
      cartCache = null;
    }
  };

  // Handles all eight Shopify money placeholders. The previous version knew four,
  // so Sweden ({{amount_with_space_separator}}) printed the raw template and
  // comma formats grouped thousands wrongly (DK "1,570,00 kr"). Fixed 23-Sep-2026.
  function printMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || window.Currency?.format || window.Currency?.moneyFormat || '${{amount}}';
    const value = parseFloat(cents) / 100;

    function group(n, decimals, thousands, decimal) {
      const parts = (isNaN(n) ? 0 : n).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }

    const map = {
      amount: group(value, 2, ',', '.'),
      amount_no_decimals: group(value, 0, ',', '.'),
      amount_with_comma_separator: group(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: group(value, 0, '.', ','),
      amount_with_apostrophe_separator: group(value, 2, "'", '.'),
      amount_no_decimals_with_space_separator: group(value, 0, ' ', ','),
      amount_with_space_separator: group(value, 2, ' ', ','),
      amount_with_period_and_space_separator: group(value, 2, ' ', '.')
    };

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) { return key in map ? map[key] : m; });
  }

  function t(key) {
    return window.Language?.strings?.[key] || key;
  }

  function queryReplaceParam(key, value) {
    const url = new URL(window.location.href);
    if (value === null || value === undefined || value === '') {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
    return url.pathname + url.search;
  }

  // Preserve window.Cart.data set by Liquid inline script (runs before deferred JS)
  const existingCartData = window.Cart?.data;

  window.CartAPI = CartAPI;
  window.printMoney = printMoney;
  window.t = t;
  window.queryReplaceParam = queryReplaceParam;
  window.Cart = CartAPI;

  // Restore Cart.data for backward compat + use as initial cache
  if (existingCartData) {
    window.Cart.data = existingCartData;
    cartCache = existingCartData;
  }
})();
